import dayjs from 'dayjs'
export type Value = { base: string | null; sub: string | null; label: string | null }
// types you already have
export type Platform =
  | 'Google'
  | 'Meta'
  | 'Microsoft'
  | 'LinkedIn'
  | 'Simplifi'
  | 'YouTube'
  | 'GTM'
  | 'GA4'
  | 'Website'
  | 'Other'
  | 'SEO'
export type Channel =
  | 'Search'
  | 'Social'
  | 'Display'
  | 'Video'
  | 'SEO'
  | 'Integration'
  | 'Website'
  | 'Creative'
  | 'Digital'
  | 'Project'

// helpful constants for guards
export const ALL_PLATFORMS: Platform[] = [
  'Google',
  'Meta',
  'Microsoft',
  'LinkedIn',
  'Simplifi',
  'YouTube',
  'GTM',
  'GA4',
  'Website',
  'SEO',
  'Other',
]
export const DIGITAL_CHANNELS: Partial<Channel>[] = [
  'Search',
  'Social',
  'Display',
  'Video',
  'SEO',
  'Integration',
  'Digital',
]
export const ALL_CHANNELS: Channel[] = [
  'Search',
  'Social',
  'Display',
  'Video',
  'SEO',
  'Integration',
  'Website',
  'Creative',
  'Digital',
]
export type TaskTypeId =
  | 'budget'
  | 'createSetup'
  | 'updates'
  | 'newClientOnboard'
  | 'review'
  | 'campaignChecklists'
  | 'adApprovals'
  | 'integration'
  | 'creativeProject'

export type SubOption = string

export type TaskTypeDefinition = {
  id: TaskTypeId
  label: string
  // Which (platform, channel) combos can see this task type?
  allowed: Array<{ platform?: Platform | Platform[]; channel?: Channel | Channel[] }> // omit = wildcard
  // Sub-options (what your Two-Pane shows). Can be static or dynamic per (platform, channel).
  subOptions: SubOption[] | ((ctx: { platform: Platform; channel: Channel }) => SubOption[])
  // Optional: checklist items per sub-option (used by campaignChecklists)
  checklistBySub?: Record<SubOption, string[]>
  // Default description (HTML) per selection
  makeDescription?: (ctx: {
    platform: Platform
    channel: Channel
    subOption?: SubOption | null
    orgName?: string
    productTitle?: string
    start?: string | null
    due?: string | null
  }) => string
}

// ---------- Libraries of options ----------

export const isPlatform = (v: unknown): v is Platform => ALL_PLATFORMS.includes(v as Platform)
export const isChannel = (v: unknown): v is Channel => ALL_CHANNELS.includes(v as Channel)
export const asPlatformOrEmpty = (raw?: string): Platform | null => (isPlatform(raw) ? (raw as Platform) : null)
export const asChannelOrEmpty = (raw?: string): Channel | null => (isChannel(raw) ? (raw as Channel) : null)

export type DescCtx = { kind: 'prod'; idx: number } | { kind: 'org'; idx: number } | null
export const makeProdCtx = (idx: number): DescCtx => ({ kind: 'prod', idx })
export const makeOrgCtx = (idx: number): DescCtx => ({ kind: 'org', idx })

export type CreativeKey = keyof CreativeTypeOptions

export type ProductLike = {
  title?: string
  productName?: string
  category?: string | null
  subcategory?: string | null
  deal_uuid?: string | null
}
export type Row = {
  name?: string
  kind?: 'prod' | 'org'
  org: any | null
  platform: Platform | null
  channel: Channel | null
  deal_uuid: string | null
  taskType: string
  subType?: string
  taskTypeLabel: string
  startDate: dayjs.Dayjs | null
  endDate: dayjs.Dayjs | null
  dateDue: dayjs.Dayjs | null
  addtlNotes: string
  /** Short summary of extra context for the task (displayed in grid Details column) */
  details?: string
  assigner: string | null
  assignees: any[]
  queue: any | null
  checklist: { id: string; label: string; done: boolean; createdTime: string }[]
  progress: { total: number; done: number }
  selectedProduct: any | null
  creative?: {
    typeKey: CreativeKey // e.g., 'logoDesign'
    values: Record<string, any> // fieldId -> value (string | string[] | number ...)
  }
  /* NEW */
  text1?: string
  text2?: string
  text3?: string
  text4?: string
  isOnboarding?: boolean
}
export type OrgRow = {
  name?: string
  kind?: 'prod' | 'org'
  org: any | null
  platform?: string
  channel?: string
  deal_uuid?: string | null
  taskType: string
  subType?: string
  taskTypeLabel: string
  startDate: dayjs.Dayjs | null
  endDate?: dayjs.Dayjs | null
  dateDue: dayjs.Dayjs | null
  addtlNotes: string
  /** Short summary of extra context for the task (displayed in grid Details column) */
  details?: string
  assigner: string | null
  assignees: string[]
  queue?: any | null
  checklist?: { id: string; label: string; done: boolean; createdTime: string }[]
  progress?: { total: number; done: number }
  selectedProduct?: any | null
  isOnboarding?: boolean
  productSK?: string
}
export type DescKey = {
  platform?: string
  channel?: string
  taskType: string
  taskTypeLabel?: string
  subType?: string
}
export type TaskValue = { base: TaskType; label: string; sub?: string }

// taskTypes.ts
// export const TASK_TYPE_TREE: Record<string, string[]> = {
//   budget: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
//   createSetup: ['Performance', 'Ad Copy', 'Website', 'Keywords', 'Search Terms', 'Campaign Settings', 'Other'],
//   updates: ['Performance', 'Ad Copy', 'Website', 'Keywords', 'Search Terms', 'Campaign Settings', 'Other'],
//   newClientOnboard: ['Quality Assurance', 'Other'],
//   review: [
//     'Website',
//     'Keywords / Keyword Rankings',
//     'Name/Address/Phone Number/Business Hours',
//     'Google Business Profile',
//   ],
//   campaignChecklists: ['Checklist'],
//   adApprovals: [],
//   Integration: ['GTM', 'GA4', 'Triggers / Tags', 'Events', 'Conversion Tracking'],
// }

// export const DEFAULT_CAMPAIGN_CHECKLIST: Record<string, string[]> = {
//   Meta: [
//     'Campaign Structure document',
//     'Remarketing Audience created',
//     'Page Linked',
//     'URL/UTM',
//     'Ads Optimized for Placements',
//     'Ads Approved/ Spelling checked',
//     'Website Events Check Marked',
//     'Custom Conversions Created',
//     'Shape Setup',
//     'Budget Correct',
//   ],
//   googleSearch: [
//     'Naming Convention',
//     'Ads Review',
//     'Keywords',
//     'Negative Keywords',
//     'Ad Extensions',
//     'Locations',
//     'Budget and Bids',
//     'Analytics Linked',
//     'Goals Imported',
//     'Shape Setup',
//     'Account Settings',
//   ],
//   Display: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
//   Video: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
//   SEO: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
//   Integration: ['Increase', 'Decrease', 'Recommendation', 'Audit'],
// }
export interface CreativeTypeOptions {
  socialAds: Record<string, any>
  displayAds: Record<string, any>
  organicSocial: Record<string, any>
  photography: Record<string, any>
  printDesign: Record<string, any>
  designMaterials: Record<string, any>
  website: Record<string, any>
  websiteDesignOverview: Record<string, any>
  websiteStructure: Record<string, any>
  video: Record<string, any>
  fullBrandBuild: Record<string, any>
  logoDesign: Record<string, any>
  marketingCollateral: Record<string, any>
}
export const SPECIFIC_TEMPLATES: Record<string, string[]> = {
  'Google|Search|updates': [
    'Performance',
    'Ad Copy',
    'Website',
    'Audience',
    'Targeting',
    'Campaign Settings',
    'Placements',
    'Other',
  ],
}
export const PLATFORM_OPTIONS = [
  'Google',
  'Meta',
  'LinkedIn',
  'Microsoft',
  'Simplifi',
  'YouTube',
  'GTM',
  'GA4',
  'Website',
] as const
export const PLATFORM_TO_CHANNELS: Record<string, string[]> = {
  Google: ['Search', 'Display', 'SEO'],
  Meta: ['Social', 'Video'],
  Microsoft: ['Search'],
  LinkedIn: ['Social'],
  Simplifi: ['Display', 'Video'],
  YouTube: ['Video'],
  GTM: ['Social'],
  Website: ['Website'],
}

// Limit task-types to those you listed
export const ALLOWED_TASK_TYPES = [
  'budget',
  'createSetup',
  'updates',
  'newClientOnboard',
  'review',
  'campaignChecklists',
  'adApprovals',
  'integration',
  'creativeIntake',
  'concept',
  'design',
  'proofs',
  'revisions',
  'assetHandoff',
  'finalDelivery',
  'photography',
] as const
export type TaskType = typeof ALLOWED_TASK_TYPES[number]
// --- Default description snippets (HTML) ---
